import styles from "./ShopNav.module.scss";
import { useState, useRef, useEffect } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useSortBy, RangeInput, useInstantSearch, useRefinementList, useClearRefinements, useSearchBox } from "react-instantsearch-hooks-web";
import RefinementAccordion from "./RefinementAccordion";
import Button from "../../components/Button";
import CustomRefinementList from "../../components/refinement-list";
import Toggle from "../../components/toggle";
import useScrollDirection from "../../hooks/useScrollDirection";

import Sliders from "/packages/icons/sliders.svg";
import Xmark from "/packages/icons/xmark.svg";
import Grid from "/packages/icons/grid.svg";
import List from "/packages/icons/list.svg";
import Search from "/packages/icons/search.svg";
import ChevronUp from "/packages/icons/chevron-up.svg";
import ChevronDown from "/packages/icons/chevron-down.svg";



import dynamic from "next/dynamic";
const RangeSlider = dynamic(() => import("../../components/range-slider"), { ssr: false });

export default function ShopNav({ url, list, setList, showCarousels }) {
  const [openBackdrop, setOpenBackdrop] = useState();
  const isBreakPoint = useMediaQuery(768);
  const [allFiltersModal, setAllFiltersModal] = useState(false);
  const scrollUp = useScrollDirection(0);

  // for hiding on brand pages
  const isBrandPage = url?.includes('/brands/');

  // for hiding the category accordion on category pages
  const shouldRenderCategoryAccordion = !url?.includes("/shop/");

  const ref = useRef(null);

  const handleClick = () => {
    if (isBreakPoint) {
      const yOffset = -0; // Offset in pixels
      const element = ref.current;
      const yCoordinate = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: yCoordinate, behavior: 'smooth' });
    } else return
  };

  return (
    <>
      <div className={`${styles.backdrop} ${openBackdrop ? styles.backdrop__active : undefined} `}
      />
      <nav
        className={`${styles.wrapper} ${scrollUp ? styles.show : styles.hide}`}
        onClick={handleClick}
        ref={ref}
      >
          <div id="shopNav" className={`${styles.nav}`}>
            <div className={styles.filterButtons}>
              <SearchButton />

              <Button type="button" ariaLabel="all filters" className="filter filter--icon" onClick={() => { setAllFiltersModal(!allFiltersModal) }} >
                <Sliders />
              </Button>

              <RefinementAccordion
                title="Sale"
                attribute="applicable_special_ids"
                limit={100}
                setOpenBackdrop={setOpenBackdrop}
              />

              {shouldRenderCategoryAccordion ?
                <RefinementAccordion
                  title="category"
                  attribute="root_types"
                  limit={100}
                  sortBy={["name"]}
                  setOpenBackdrop={setOpenBackdrop}
                />
                :
                <RefinementAccordion
                  title="subcategory"
                  attribute="root_types"
                  limit={100}
                  sortBy={["name"]}
                  setOpenBackdrop={setOpenBackdrop}
                />
              }

              {!isBrandPage &&
                <RefinementAccordion
                  title="Brand"
                  attribute="brand"
                  limit={200}
                  sortBy={["name:asc"]}
                  search={true}
                  setOpenBackdrop={setOpenBackdrop}
                />
              }

              <RefinementAccordion
                title="Lineage"
                sortBy={["name:asc"]}
                attribute="category"
                setOpenBackdrop={setOpenBackdrop}
              />

              <RefinementAccordion
                title="Weight"
                attribute="available_weights"
                setOpenBackdrop={setOpenBackdrop}
              />

            </div>

            <div className="visually-hidden">
              <RangeInput attribute="bucket_price" />
              <RangeInput attribute="percent_thc" />
              <RangeInput attribute="percent_cbd" />
            </div>

            {!showCarousels && (
              <div className="flex center gap-100 wrap" style={{ marginLeft: 'auto', justifyContent: 'flex-end' }} >
                <CustomSortBy
                  classNames={{
                    root: 'sortBy',
                    select: 'sortBySelect',
                    option: 'sortByOption',
                  }}
                  items={[
                    // { value: 'menu-products-by-category-and-name-asc-production', label: 'Products A-Z' },
                    { value: 'menu-products-by-rating-production', label: 'Rating' },
                    { value: 'menu-products-production', label: 'Brands A-Z' },
                    { value: 'menu-products-by-price-production', label: 'Price Low to High' },
                    { value: 'menu-products-by-price-desc-production', label: 'Price High to Low' },
                    { value: 'menu-products-by-thc-potency-asc-production', label: 'THC% Low to High' },
                    { value: 'menu-products-by-thc-potency-desc-production', label: 'THC% High to Low' },
                  ].filter(item => !(isBrandPage && item.value === 'menu-products-production'))} // hide brands A-Z on brands pages
                />

                <div className={styles.gridListButton}>
                  <Button type="button" className="filter" onClick={() => setList(!list)}>
                    {list ?
                      <> <Grid /> Grid</>
                      :
                      <> <List /> List</>
                    }
                  </Button>
                </div>

              </div>
            )}
          </div>
      </nav>
      <AllFiltersModal
        shouldRenderCategoryAccordion={!url?.includes("/shop/")}
        isBrandPage={isBrandPage}
        allFiltersModal={allFiltersModal}
        setAllFiltersModal={setAllFiltersModal}
      />

    </>
  );
}

function ClearRefinements({ setAllFiltersModal }) {
  const { refine } = useClearRefinements();
  const handleClick = () => {
    refine()
    setAllFiltersModal(false)
  }
  return (
    <Button type="button" className="danger" onClick={handleClick}>Clear All</Button>
  )

}

function Stats() {
  const { results } = useInstantSearch();
  return (
    <span className={styles.productCount}>
      {results.nbHits === 0 ? "No Products" : `Show ${results.nbHits} ${results.nbHits > 1 ? "products" : "product"}`}
    </span>
  )
}

function AllFiltersModal({ shouldRenderCategoryAccordion, isBrandPage, allFiltersModal, setAllFiltersModal }) {
  const [showStats, setShowStats] = useState(false);

  // Get the number of items for each refinement list
  const { items: specialsItems } = useRefinementList({ attribute: "applicable_special_ids" });
  const { items: categoryItems } = useRefinementList({ attribute: "root_types" });
  const { items: lineageItems } = useRefinementList({ attribute: "category" });
  const { items: weightItems } = useRefinementList({ attribute: "available_weights" });
  const { items: effectItems } = useRefinementList({ attribute: "feelings" });

  // Show stats after hydration to prevent errors
  useEffect(() => {
    setShowStats(true);
  }, []);

  return (
    <div className={`${styles.allFiltersModal} ${allFiltersModal ? styles.visible : styles.hidden}`}>
      <div
        className={`${styles.allFiltersBackdrop} ${allFiltersModal ? styles.allFiltersBackdrop__active : ""}`} 
        onClick={() => setAllFiltersModal(false)}
      />
      <div className={styles.allFilters}>
        <div className={styles.allFiltersInner}>
          <div className={styles.closeModal}>
            <Button
              type="button"
              className="icon danger-alt"
              ariaLabel="close filters"
              onClick={() => setAllFiltersModal(false)}
            >
              <Xmark />
            </Button>
          </div>
          <div className={styles.modalTitle}>Filters</div>

          <div className={styles.filters}>
            {/* Only render Toggle if specials exist */}
            {specialsItems.length > 0 && (
              <Toggle title="Sale">
                <CustomRefinementList title="Sale" attribute="applicable_special_ids" limit={100} />
              </Toggle>
            )}

            {shouldRenderCategoryAccordion && categoryItems.length > 0 ? (
              <Toggle title="Category">
                <CustomRefinementList title="category" attribute="root_types" limit={100} sortBy={["name"]} />
              </Toggle>
            ) : categoryItems.length > 0 && (
              <Toggle title="Subcategory">
                <CustomRefinementList title="subcategory" attribute="root_types" limit={100} sortBy={["name"]} />
              </Toggle>
            )}

            {!isBrandPage && (
              <Toggle title="Brand">
                <CustomRefinementList title="Brand" attribute="brand" limit={200} sortBy={["name:asc"]} search={true} />
              </Toggle>
            )}

            {lineageItems.length > 0 && (
              <Toggle title="Lineage">
                <CustomRefinementList attribute="category" />
              </Toggle>
            )}

            {weightItems.length > 0 && (
              <Toggle title="Weight">
                <CustomRefinementList title="Weight" attribute="available_weights" limit={100} sortBy={["asc"]} />
              </Toggle>
            )}

            {effectItems.length > 0 && (
              <Toggle title="Effect">
                <CustomRefinementList title="Effect" attribute="feelings" limit={100} sortBy={["asc"]} />
              </Toggle>
            )}

            {/* <Toggle title="Rating">
              <CustomRefinementList title="Rating" attribute="aggregate_rating" limit={100} sortBy={["asc"]} />
            </Toggle> */}

            <Toggle title="Price">
              <div style={{ padding: "0rem 2rem 2rem 0rem" }}>
                <RangeSlider range title="Price" attribute="bucket_price" limit={100} />
              </div>
            </Toggle>

            <Toggle title="THC %">
              <div style={{ padding: "0rem 2rem 2rem 0rem" }}>
                <RangeSlider range title="THC" attribute="percent_thc" limit={100} />
              </div>
            </Toggle>
          </div>

          <div className={styles.allFiltersButtons}>
            <ClearRefinements setAllFiltersModal={setAllFiltersModal} />
            <Button type="button" className="primary" onClick={() => setAllFiltersModal(false)}>
              {showStats && <Stats />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomSortBy({ items }) {
  const { currentRefinement, options, refine } = useSortBy({ items });
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (label) => {
    const option = options.find((opt) => opt.label === label);
    refine(option.value);
    toggleDropdown();
  };

  const currentLabel = options.find((opt) => opt.value === currentRefinement)?.label || '';
  const icon = isOpen ? <ChevronUp /> : <ChevronDown />;

  return (
    <div className={styles.sortBy}>
      <div className={styles.dropdown} onClick={toggleDropdown}>
        <div className={`${styles.selectedOption} ${isOpen ? styles.open : ''}`}>
          Sort: {currentLabel} {icon}
        </div>
        {isOpen && (
          <div className={styles.options}>
            {options.map((option) => (
              <div
                key={option.value}
                className={styles.option}
                onClick={() => handleOptionClick(option.label)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function SearchButton() {
  const { refine } = useSearchBox();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef(null);
  const searchWrapperRef = useRef(null);

  const toggleSearch = (e) => {
    e.stopPropagation();

    // Only close if clicking on the button while open
    if (isSearchOpen && e.target.closest(`.${styles.searchButton}`)) {
      // setIsSearchOpen(false);
    } else {
      setIsSearchOpen(true);
      inputRef.current?.focus();
    }
  };

  const clearSearch = (e) => {
    e.stopPropagation();
    setSearchTerm('');
    refine('');
    inputRef.current?.focus();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    refine(searchTerm);
    setIsSearchOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(event.target)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div
      className={styles.searchWrapper}
      ref={searchWrapperRef}
      onClick={toggleSearch} // Clicking anywhere opens it
    >
      <form
        onSubmit={handleSearchSubmit}
        className={`${styles.searchForm} ${isSearchOpen ? styles.open : ''}`}
      >
        <button type="button" className={styles.searchButton}>
          <Search />
        </button>

        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search products"
          className={styles.searchInput}
        />

        {isSearchOpen && searchTerm.length > 0 && (
          <button type="button" className={styles.clearButton} onClick={clearSearch}>
            <Xmark />
          </button>
        )}
      </form>
    </div>
  );
}




